import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { intIdToUuid } from "../../../../../../helpers/uuid";
import apiClient from "../../../../../../helpers/api-client";
import { filterSettledPromises } from "../../../../../../helpers";
import { extractAssessmentName, isPortalCreatedThirdPartyAssessment } from "../../../../../../helpers/assessment-name";
import { PortalContext } from "../../../../../portal-config";
import { isStandaloneAssessment, QuizWithProctoringOptions } from "../../../assessments-page-helper";
import { ProctoringAssessmentConfigurationV2 } from "@verity/proctoring-option/types";

export type LiveSession = {
	id: string;
	name: string;
	startTime: string;
	endTime: string;
	registrationStartTime?: string;
	registrationEndTime?: string;
	users: { userId: string }[];
	createdAt: string;
	started: boolean;
};

type AssessmentsResponse = QuizWithProctoringOptions[];

export type Assessment = QuizWithProctoringOptions & {
	name?: string;
	lmsProvidedCourseId?: string;
	attemptsCount: number;
	liveSessions: LiveSession[];
	proctoringAssessmentConfiguration?: ProctoringAssessmentConfigurationV2 | null;
};

type AssessmentWithoutStarted = Omit<Assessment, "liveSessions"> & { liveSessions: Omit<LiveSession, "started">[] };

type AssessmentsData = {
	map: Record<number, AssessmentWithoutStarted>;
	ids: number[];
	loadingIds: number[];
};

type BatchAssessmentDetails = {
	assessments: AssessmentWithoutStarted[];
	courses: Record<number, { courseId: string; accessControlUuid: string }>;
	liveSessions: Record<number, Omit<LiveSession, "started">[]>;
	resultCounts: Record<string, number>;
	thirdPartyUrls: Record<number, string | undefined>;
};

const BATCH_SIZE = 5;

async function fetchAssessmentDetails(
	{ lms, instituteIntegrationHostname, instituteUuid, courseUuid, lmsProvidedCourseId }: PortalContext,
	currData: AssessmentsData,
): Promise<BatchAssessmentDetails> {
	const { map, loadingIds } = currData;
	if (loadingIds.length === 0) {
		return { assessments: [], courses: {}, liveSessions: {}, resultCounts: {}, thirdPartyUrls: {} };
	}
	const batchAssessments = loadingIds.slice(0, BATCH_SIZE).map((id) => map[id]);

	const courses = Object.fromEntries(
		filterSettledPromises(
			await Promise.allSettled(
				[...new Set(batchAssessments.map(({ courseId }) => courseId))].map(async (courseId) => [
					courseId,
					await apiClient.course.getCourseInfoFromId(courseId),
				]),
			),
		),
	);
	const liveSessions: Record<number, Omit<LiveSession, "started">[]> = Object.fromEntries(
		filterSettledPromises(
			await Promise.allSettled(
				batchAssessments
					.filter(({ proctoringOptions }) => proctoringOptions?.isLiveProctored)
					.map(async ({ id, courseId }) => {
						const assessmentCourseUuid = courses[courseId]?.accessControlUuid ?? courseUuid;
						return [
							id,
							(
								await apiClient.liveClient.getLiveSessionsWithUsers({
									instituteUuid,
									courseUuid: assessmentCourseUuid,
									assessmentUuid: intIdToUuid(id),
									sortBy: "startTime",
								})
							).results.map((session) => ({
								...session,
								registrationStartTime: session.registrationStartTime ?? undefined,
								registrationEndTime: session.registrationEndTime ?? undefined,
							})),
						];
					}),
			),
		),
	);
	const resultCounts = Object.fromEntries(
		filterSettledPromises(
			await Promise.allSettled(
				batchAssessments.map(async ({ id, courseId, quizId }) => [
					id,
					await apiClient.proctoringSession.getOwnResultsCount(courseId, quizId),
				]),
			),
		),
	);

	const thirdPartyUrls: Record<number, string | undefined> = Object.fromEntries(
		filterSettledPromises(
			await Promise.allSettled(
				batchAssessments
					.filter((assessment) => isPortalCreatedThirdPartyAssessment(assessment))
					.map(async ({ id, quizId }) => [
						id,
						(
							await apiClient.verityOptions.get({
								lms,
								hostname: instituteIntegrationHostname,
								courseId: lmsProvidedCourseId,
								quizId,
							})
						).thirdPartyAssessmentOptions?.thirdPartyUrl ?? undefined,
					]),
			),
		),
	);

	return { assessments: batchAssessments, courses, liveSessions, resultCounts, thirdPartyUrls };
}

function addAssessmentDetails(
	{ lmsProvidedCourseId }: PortalContext,
	{ map, ids, loadingIds }: AssessmentsData,
	{ assessments, thirdPartyUrls, courses, resultCounts, liveSessions }: BatchAssessmentDetails,
) {
	const filteredAssessments = assessments.filter((assessment) => {
		const { id, proctoringOptions } = assessment;
		return (
			(!proctoringOptions?.isLiveProctored || (liveSessions[id]?.length ?? 0) > 0) &&
			(!isPortalCreatedThirdPartyAssessment(assessment) || thirdPartyUrls[id])
		);
	});
	filteredAssessments.forEach((assessment) => {
		const { id, courseId } = assessment;
		map[id] = {
			...assessment,
			lmsProvidedCourseId: courses[courseId]?.courseId ?? lmsProvidedCourseId,
			attemptsCount: resultCounts[id] ?? 0,
			liveSessions: liveSessions[id] ?? [],
		};
		ids.push(id);
	});
	loadingIds.splice(0, BATCH_SIZE);
}

const assessmentsApi = createApi({
	reducerPath: "assessments",
	baseQuery: async (arg, api, extraOptions) =>
		fetchBaseQuery({
			fetchFn: apiClient.fetch.bind(apiClient),
			baseUrl: `/`,
			prepareHeaders: (headers) => {
				headers.set("Content-Type", "application/json");
				return headers;
			},
		})(arg, api, extraOptions),
	tagTypes: ["Assessments", "Assessment", "ActiveLiveSessionIds"],
	endpoints: (builder) => ({
		getAssessments: builder.query<AssessmentsData, PortalContext>({
			queryFn: async (context, _api, _extra, fetchWithBQ) => {
				const { lmsProvidedCourseId } = context;
				const [suffix, result] = await Promise.all([
					apiClient.institute.getSuffix().catch(console.error),
					fetchWithBQ(`api/verity/lms-course/${lmsProvidedCourseId}/assessments`),
				]);
				const assessmentsResp = result.data as AssessmentsResponse | undefined;

				if (!assessmentsResp) {
					return result;
				}

				const filteredAssessments: AssessmentWithoutStarted[] = assessmentsResp
					.map((assessment) => ({
						...assessment,
						name: extractAssessmentName(assessment, suffix ?? undefined),
						attemptsCount: 0,
						liveSessions: [],
					}))
					.filter((assessment) => {
						const { proctoringOptions } = assessment;
						return (
							!isStandaloneAssessment(assessment) &&
							(!proctoringOptions?.enabled ||
								proctoringOptions?.isExtension ||
								proctoringOptions?.isLockdown ||
								proctoringOptions?.isMobile)
						);
					});
				filteredAssessments.sort((a, b) => b.id - a.id);

				const filteredAssessmentIds = filteredAssessments.map(({ id }) => id);

				const data = {
					map: Object.fromEntries(filteredAssessments.map((assessment) => [assessment.id, assessment])),
					ids: [],
					loadingIds: filteredAssessmentIds,
				};
				addAssessmentDetails(context, data, await fetchAssessmentDetails(context, data));
				return {
					...result,
					data,
				} as any;
			},
			onCacheEntryAdded: async (context, { cacheDataLoaded, updateCachedData, cacheEntryRemoved, getCacheEntry }) => {
				let stopLoading = false;
				cacheEntryRemoved.then(() => {
					stopLoading = true;
				});
				let data: AssessmentsData | undefined = (await cacheDataLoaded).data;
				while (!stopLoading && data && data.loadingIds.length > 0) {
					const details = await fetchAssessmentDetails(context, data);
					updateCachedData((draft) => {
						addAssessmentDetails(context, draft, details);
					});
					data = getCacheEntry().data;
				}
			},
			providesTags: (result, _error, { lmsProvidedCourseId }) => [
				...(result
					? Object.keys(result).map((id) => ({
							type: "Assessment" as const,
							id,
						}))
					: []),
				{ type: "Assessments", id: lmsProvidedCourseId },
			],
		}),
		getActiveLiveSessionIds: builder.query<string[], string[]>({
			queryFn: async (liveSessionIds, _api, _extra, fetchWithBQ) => {
				return {
					data: (
						await Promise.allSettled(
							liveSessionIds.map(
								async (id) =>
									[id, await fetchWithBQ(`api/live/liveSession/${id}/liveState`)] satisfies [
										string,
										{
											data?: unknown;
										},
									],
							),
						)
					)
						.filter(
							(
								result,
							): result is PromiseFulfilledResult<
								[
									string,
									{
										data: {};
									},
								]
							> => result.status === "fulfilled" && !!result.value[1].data,
						)
						.map(({ value }) => value[0]),
				};
			},
			providesTags: [{ type: "ActiveLiveSessionIds" }],
		}),
		registerForLiveSession: builder.mutation<
			void,
			PortalContext & {
				liveSessionId: string;
				userId: string;
			}
		>({
			query: ({ liveSessionId, userId }) => ({
				url: `api/live/liveSession/${liveSessionId}/user`,
				method: "POST",
				body: {
					userId,
					role: "student",
				},
			}),
			onQueryStarted: async ({ liveSessionId, userId, ...portalContext }, { queryFulfilled, dispatch }) => {
				await queryFulfilled;
				dispatch(
					assessmentsApi.util.updateQueryData("getAssessments", portalContext, (draft) => {
						Object.values(draft.map).forEach((assessment) => {
							assessment.liveSessions.forEach((session) => {
								if (session.id === liveSessionId) {
									session.users.push({ userId });
								}
							});
						});
					}),
				);
			},
		}),
		unregisterForLiveSession: builder.mutation<
			void,
			PortalContext & {
				liveSessionId: string;
				userId: string;
			}
		>({
			query: ({ liveSessionId, userId }) => ({
				url: `api/live/liveSession/${liveSessionId}/user/${userId}`,
				method: "DELETE",
			}),
			onQueryStarted: async ({ liveSessionId, userId, ...portalContext }, { queryFulfilled, dispatch }) => {
				await queryFulfilled;
				dispatch(
					assessmentsApi.util.updateQueryData("getAssessments", portalContext, (draft) => {
						Object.values(draft.map).forEach((assessment) => {
							assessment.liveSessions.forEach((session) => {
								if (session.id === liveSessionId) {
									session.users = session.users.filter((user) => user.userId !== userId);
								}
							});
						});
					}),
				);
			},
		}),
	}),
});

export default assessmentsApi;
